import TextInput from '~/components/TextInput';
import TextArea from '~/components/TextArea';
import useCurrentBusiness from '~/lib/useCurrentBusiness';
import { useInvoiceContext } from '~/lib/InvoiceContext';
import { useInvoicesQuery } from '~/lib/Queries';
import type { QuestionSettings } from '~/lib/Questions/Questions.types';
import { useMemo } from 'react';

interface EditInvoiceHeaderFormProps {
    settings: QuestionSettings;
    setSettings: (settings: QuestionSettings) => void;
}

const EditInvoiceHeaderForm = ({ settings, setSettings }: EditInvoiceHeaderFormProps) => {
    const business = useCurrentBusiness();
    const businessName = settings?.businessName || business?.name || '';
    const businessAddress = settings?.businessAddress || business?.physical_address || '';
    const invoiceContext = useInvoiceContext();
    const updateInvoice = invoiceContext?.updateInvoice;
    const invoice = invoiceContext?.invoice;

    // Use invoice_number from invoice model if available, otherwise fall back to settings
    const invoiceNumber = invoice?.invoice_number || settings?.invoiceNumber || '';

    // Fetch all invoices to check for duplicate invoice numbers
    const { data: invoicesData } = useInvoicesQuery();
    const duplicateInvoiceNumber = useMemo(() => {
        if (!invoiceNumber) return false;
        const num = parseInt(invoiceNumber);
        if (isNaN(num)) return false;
        const invoices = invoicesData?.invoices || [];
        return invoices.some(
            (inv: any) => inv.invoice_number === num && inv.id !== invoice?.id
        );
    }, [invoiceNumber, invoicesData, invoice?.id]);

    const updateSetting = (field: string, value: any) => {
        setSettings({ ...settings, [field]: value });

        // If updating invoice number, also update the invoice model
        if (field === 'invoiceNumber' && updateInvoice) {
            updateInvoice({ invoice_number: value });
        }

        // If updating due date, also update the invoice model
        if (field === 'dueDate' && updateInvoice) {
            updateInvoice({ due_at: value });
        }
    };

    return (
        <div>
            <TextInput
                label="Business Name"
                value={businessName}
                onChange={(value) => setSettings({ ...settings, businessName: value })}
                placeholder={business?.name || 'Enter business name'}
            />

            <TextArea
                label="Business Address"
                value={businessAddress}
                onChange={(value) => setSettings({ ...settings, businessAddress: value })}
                placeholder={business?.physical_address || 'Enter business address'}
                className="mt-4"
                rows={3}
            />

            <TextInput
                label="Invoice Number"
                value={invoiceNumber}
                onChange={(value) => updateSetting('invoiceNumber', value)}
                placeholder="Leave empty to use auto-incrementing number"
                className="mt-4"
            />
            {duplicateInvoiceNumber ? (
                <div className="text-xs text-red-600 mt-1 px-3">
                    This invoice number is already in use by another invoice
                </div>
            ) : (
                <div className="text-xs text-gray-500 mt-1 px-3">
                    Leave empty to use the automatically assigned incrementing invoice number
                </div>
            )}
        </div>
    );
};

export default EditInvoiceHeaderForm;

